/**
 * MEVIA M12 - Job Profiles Manager
 * Admin panel for managing job position profiles and employee assignments.
 * Profiles define metacapital requirements for each position.
 * IAP (Job Adequacy Index) is calculated by comparing employee scores vs profile requirements.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getToken } from '../utils/tokenStorage';import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Briefcase, Users, Plus, Pencil, Trash2, UserPlus, ChevronLeft, Search, Check, X } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';


const CAPITALS = [
  { key: 'physical', label: { es: 'Fisico', en: 'Physical' }, color: '#ef4444' },
  { key: 'human', label: { es: 'Humano', en: 'Human' }, color: '#f97316' },
  { key: 'manufactured', label: { es: 'Manufacturado', en: 'Manufactured' }, color: '#eab308' },
  { key: 'financial', label: { es: 'Financiero', en: 'Financial' }, color: '#22c55e' },
  { key: 'natural', label: { es: 'Natural', en: 'Natural' }, color: '#14b8a6' },
  { key: 'cultural', label: { es: 'Cultural', en: 'Cultural' }, color: '#3b82f6' },
  { key: 'social', label: { es: 'Social', en: 'Social' }, color: '#6366f1' },
  { key: 'knowledge', label: { es: 'Conocimiento', en: 'Knowledge' }, color: '#8b5cf6' },
  { key: 'psychological', label: { es: 'Psicologico', en: 'Psychological' }, color: '#ec4899' },
  { key: 'purpose', label: { es: 'Proposito', en: 'Purpose' }, color: '#f43f5e' },
];

const T = {
  es: {
    title: 'Perfiles de Puesto',
    subtitle: 'Define los requerimientos de cada puesto y asigna empleados para calcular el IAP',
    profiles: 'Perfiles',
    assignments: 'Asignaciones',
    newProfile: 'Nuevo Perfil',
    editProfile: 'Editar Perfil',
    jobTitle: 'Titulo del Puesto',
    department: 'Departamento',
    description: 'Descripcion',
    tokenDistribution: 'Distribucion de Tokens (debe sumar 100)',
    total: 'Total',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    deleteConfirm: 'Seguro que deseas eliminar este perfil?',
    employees: 'empleados',
    noProfiles: 'No hay perfiles creados. Crea el primero.',
    search: 'Buscar empleado...',
    assigned: 'Asignados',
    unassigned: 'Sin asignar',
    assignProfile: 'Asignar Perfil',
    removeAssign: 'Quitar asignacion',
    allDepartments: 'Todos',
    iapExplain: 'El IAP se calcula automaticamente al comparar las evaluaciones del empleado con el perfil del puesto.',
    back: 'Volver al Dashboard',
    created: 'Perfil creado exitosamente',
    updated: 'Perfil actualizado',
    deleted: 'Perfil eliminado',
    assignSuccess: 'Perfil asignado exitosamente',
    unassignSuccess: 'Asignacion removida',
    validationError: 'Los tokens deben sumar 100',
  },
  en: {
    title: 'Job Profiles',
    subtitle: 'Define position requirements and assign employees to calculate IAP',
    profiles: 'Profiles',
    assignments: 'Assignments',
    newProfile: 'New Profile',
    editProfile: 'Edit Profile',
    jobTitle: 'Job Title',
    department: 'Department',
    description: 'Description',
    tokenDistribution: 'Token Distribution (must sum to 100)',
    total: 'Total',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this profile?',
    employees: 'employees',
    noProfiles: 'No profiles created yet. Create the first one.',
    search: 'Search employee...',
    assigned: 'Assigned',
    unassigned: 'Unassigned',
    assignProfile: 'Assign Profile',
    removeAssign: 'Remove assignment',
    allDepartments: 'All',
    iapExplain: 'IAP is automatically calculated by comparing employee evaluations against the job profile.',
    back: 'Back to Dashboard',
    created: 'Profile created successfully',
    updated: 'Profile updated',
    deleted: 'Profile deleted',
    assignSuccess: 'Profile assigned successfully',
    unassignSuccess: 'Assignment removed',
    validationError: 'Tokens must sum to 100',
  }
};

const DEFAULT_SCORES = CAPITALS.reduce((acc, c) => ({ ...acc, [c.key]: 10 }), {});

export default function JobProfilesManager() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = T[language] || T.es;
  const empresaId = user?.empresa_id;
  const token = getToken();

  const [profiles, setProfiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profiles');

  // Profile form state
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({ job_title: '', department: '', description: '', scores: { ...DEFAULT_SCORES } });

  // Assignment state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterProfile, setFilterProfile] = useState('');
  const [assignDialog, setAssignDialog] = useState({ open: false, employee: null });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const [profRes, empRes] = await Promise.all([
        axios.get(`/v2/job-profiles/list/${empresaId}`, { headers }),
        axios.get(`/v2/job-profiles/employees/${empresaId}`, { headers })
      ]);
      setProfiles(profRes.data.data || []);
      setEmployees(empRes.data.data || []);
    } catch (err) {
      logger.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalTokens = Object.values(formData.scores).reduce((a, b) => a + (parseInt(b) || 0), 0);

  const handleScoreChange = (key, val) => {
    const numVal = Math.max(0, Math.min(100, parseInt(val) || 0));
    setFormData(prev => ({ ...prev, scores: { ...prev.scores, [key]: numVal } }));
  };

  const handleSave = async () => {
    if (totalTokens !== 100) { toast.error(t.validationError); return; }
    if (!formData.job_title.trim()) { toast.error('Job title required'); return; }

    try {
      if (editingProfile) {
        await axios.put(`/v2/job-profiles/update/${editingProfile.id}`, {
          job_title: formData.job_title, department: formData.department,
          description: formData.description, scores: formData.scores
        }, { headers });
        toast.success(t.updated);
      } else {
        await axios.post(`/v2/job-profiles`, {
          empresa_id: empresaId, job_title: formData.job_title,
          department: formData.department, description: formData.description,
          scores: formData.scores
        }, { headers });
        toast.success(t.created);
      }
      setShowForm(false);
      setEditingProfile(null);
      setFormData({ job_title: '', department: '', description: '', scores: { ...DEFAULT_SCORES } });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({ job_title: profile.job_title, department: profile.department || '', description: profile.description || '', scores: { ...profile.scores } });
    setShowForm(true);
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await axios.delete(`/v2/job-profiles/delete/${profileId}`, { headers });
      toast.success(t.deleted);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleAssign = async (employeeId, profileId) => {
    try {
      await axios.put(`/v2/job-profiles/assign/${employeeId}`,
        { job_profile_id: profileId || null }, { headers });
      toast.success(profileId ? t.assignSuccess : t.unassignSuccess);
      setAssignDialog({ open: false, employee: null });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const departments = [...new Set(employees.map(e => e.departamento).filter(Boolean))].sort();

  const filteredEmployees = employees.filter(e => {
    if (searchTerm && !e.nombre.toLowerCase().includes(searchTerm.toLowerCase()) && !e.email.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterDept && e.departamento !== filterDept) return false;
    if (filterProfile === 'assigned' && !e.job_profile_id) return false;
    if (filterProfile === 'unassigned' && e.job_profile_id) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="job-profiles-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-6" data-testid="job-profiles-page">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white mb-2 -ml-2" data-testid="back-to-dashboard">
          <ChevronLeft className="w-4 h-4 mr-1" />{t.back}
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Briefcase className="w-7 h-7 text-blue-400" />{t.title}
            </h1>
            <p className="text-slate-400 text-sm mt-1">{t.subtitle}</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="profiles" className="data-[state=active]:bg-blue-600" data-testid="tab-profiles">
            <Briefcase className="w-4 h-4 mr-1.5" />{t.profiles} ({profiles.length})
          </TabsTrigger>
          <TabsTrigger value="assignments" className="data-[state=active]:bg-blue-600" data-testid="tab-assignments">
            <Users className="w-4 h-4 mr-1.5" />{t.assignments}
          </TabsTrigger>
        </TabsList>

        {/* ===== PROFILES TAB ===== */}
        <TabsContent value="profiles" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingProfile(null); setFormData({ job_title: '', department: '', description: '', scores: { ...DEFAULT_SCORES } }); setShowForm(true); }}
              className="bg-blue-600 hover:bg-blue-700" data-testid="new-profile-btn">
              <Plus className="w-4 h-4 mr-1.5" />{t.newProfile}
            </Button>
          </div>

          {profiles.length === 0 ? (
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="py-12 text-center text-slate-400">{t.noProfiles}</CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map(p => (
                <ProfileCard key={p.id} profile={p} language={language} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== ASSIGNMENTS TAB ===== */}
        <TabsContent value="assignments" className="space-y-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4 space-y-3">
              <p className="text-xs text-slate-500">{t.iapExplain}</p>
              <div className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input placeholder={t.search} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-800 border-slate-700 text-white" data-testid="search-employee" />
                </div>
                <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="filter-dept">
                  <option value="">{t.allDepartments}</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select value={filterProfile} onChange={e => setFilterProfile(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="filter-assignment">
                  <option value="">{t.allDepartments}</option>
                  <option value="assigned">{t.assigned}</option>
                  <option value="unassigned">{t.unassigned}</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-1">
            {filteredEmployees.map(emp => (
              <div key={emp.id} className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border border-slate-800 rounded-lg hover:border-slate-700 transition-colors"
                data-testid={`employee-row-${emp.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{emp.nombre}</p>
                  <p className="text-xs text-slate-500">{emp.departamento || '-'}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {emp.profile_title ? (
                    <Badge variant="outline" className="border-blue-500/40 text-blue-400 text-xs">{emp.profile_title}</Badge>
                  ) : (
                    <Badge variant="outline" className="border-slate-700 text-slate-500 text-xs">{t.unassigned}</Badge>
                  )}
                  <Button size="sm" variant="ghost"
                    className={emp.profile_title ? "text-orange-400 hover:text-orange-300 h-7 px-2" : "text-blue-400 hover:text-blue-300 h-7 px-2"}
                    onClick={() => setAssignDialog({ open: true, employee: emp })}
                    data-testid={`assign-btn-${emp.id}`}>
                    {emp.profile_title ? <Pencil className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== PROFILE FORM DIALOG ===== */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProfile ? t.editProfile : t.newProfile}</DialogTitle>
            <DialogDescription className="text-slate-400">{t.tokenDistribution}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t.jobTitle} *</label>
                <Input value={formData.job_title} onChange={e => setFormData(p => ({ ...p, job_title: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white" data-testid="input-job-title" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t.department}</label>
                <Input value={formData.department} onChange={e => setFormData(p => ({ ...p, department: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white" data-testid="input-department" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t.description}</label>
              <Input value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white" data-testid="input-description" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t.tokenDistribution}</span>
                <span className={`text-sm font-bold ${totalTokens === 100 ? 'text-green-400' : 'text-red-400'}`}
                  data-testid="token-total">
                  {t.total}: {totalTokens}/100
                </span>
              </div>
              <div className="space-y-2">
                {CAPITALS.map(cap => (
                  <div key={cap.key} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cap.color }} />
                    <span className="w-28 text-xs text-slate-300">{cap.label[language] || cap.label.en}</span>
                    <div className="flex-1 relative">
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${formData.scores[cap.key]}%`, backgroundColor: cap.color }} />
                      </div>
                    </div>
                    <Input type="number" min={0} max={100} value={formData.scores[cap.key]}
                      onChange={e => handleScoreChange(cap.key, e.target.value)}
                      className="w-16 h-7 text-center text-xs bg-slate-800 border-slate-700 text-white"
                      data-testid={`score-${cap.key}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700">{t.cancel}</Button>
            <Button onClick={handleSave} disabled={totalTokens !== 100 || !formData.job_title.trim()}
              className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== ASSIGN PROFILE DIALOG ===== */}
      <Dialog open={assignDialog.open} onOpenChange={open => setAssignDialog({ open, employee: open ? assignDialog.employee : null })}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.assignProfile}</DialogTitle>
            <DialogDescription className="text-slate-400">{assignDialog.employee?.nombre}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {profiles.map(p => (
              <button key={p.id}
                onClick={() => handleAssign(assignDialog.employee?.id, p.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left
                  ${assignDialog.employee?.job_profile_id === p.id
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-700 bg-slate-800 hover:border-slate-600'}`}
                data-testid={`assign-option-${p.id}`}>
                <div>
                  <p className="text-sm font-medium">{p.job_title}</p>
                  <p className="text-xs text-slate-500">{p.department || '-'}</p>
                </div>
                {assignDialog.employee?.job_profile_id === p.id && <Check className="w-4 h-4 text-blue-400" />}
              </button>
            ))}
            {assignDialog.employee?.job_profile_id && (
              <button onClick={() => handleAssign(assignDialog.employee?.id, null)}
                className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors text-sm"
                data-testid="remove-assignment-btn">
                <X className="w-4 h-4" />{t.removeAssign}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Profile Card Sub-component =====
function ProfileCard({ profile, language, onEdit, onDelete }) {
  const scores = profile.scores || {};
  return (
    <Card className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors" data-testid={`profile-card-${profile.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base">{profile.job_title}</CardTitle>
            {profile.department && <p className="text-xs text-slate-500 mt-0.5">{profile.department}</p>}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white" onClick={() => onEdit(profile)} data-testid={`edit-profile-${profile.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" onClick={() => onDelete(profile.id)} data-testid={`delete-profile-${profile.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {profile.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{profile.description}</p>}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-1.5 mb-3">
          <Users className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs text-slate-400">{profile.employee_count || 0} {language === 'es' ? 'empleados' : 'employees'}</span>
        </div>
        <div className="space-y-1">
          {CAPITALS.map(cap => (
            <div key={cap.key} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cap.color }} />
              <span className="w-20 text-[10px] text-slate-500 truncate">{cap.label[language] || cap.label.en}</span>
              <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${scores[cap.key] || 0}%`, backgroundColor: cap.color }} />
              </div>
              <span className="text-[10px] text-slate-500 w-5 text-right">{scores[cap.key] || 0}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
